<template>
  <v-main>
    <v-container fluid>
      <!-- Stats Cards -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="4" rounded="xl" color="orange darken-1" dark>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="caption">Pending Requests</div>
                  <div class="text-h5 font-weight-bold">{{ pendingCount }}</div>
                  <div class="caption">₹{{ pendingAmount | formatAmount }}</div>
                </div>
                <v-icon size="48" color="white" style="opacity:0.4">mdi-clock-outline</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="4" rounded="xl" color="green darken-1" dark>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="caption">Approved</div>
                  <div class="text-h5 font-weight-bold">{{ approvedCount }}</div>
                  <div class="caption">₹{{ approvedAmount | formatAmount }}</div>
                </div>
                <v-icon size="48" color="white" style="opacity:0.4">mdi-check-circle-outline</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="4" rounded="xl" color="red darken-1" dark>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="caption">Rejected</div>
                  <div class="text-h5 font-weight-bold">{{ rejectedCount }}</div>
                   <div class="caption">₹{{ approvedAmount | formatAmount }}</div>
                </div>
                <v-icon size="48" color="white" style="opacity:0.4">mdi-close-circle-outline</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card elevation="4" rounded="xl" color="blue darken-1" dark>
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="caption">Total Requests</div>
                  <div class="text-h5 font-weight-bold">{{ count }}</div>
                   <div class="caption">₹{{ approvedAmount | formatAmount }}</div>
                </div>
                <v-icon size="48" color="white" style="opacity:0.4">mdi-bank-transfer-out</v-icon>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filter Bar -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Withdrawal Requests</h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="5">
                  <v-text-field
                    solo rounded
                    v-model="filter.search_keyword"
                    label="Search by name or phone..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col md="3">
                  <v-select
                    solo rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    label="Filter by Status"
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height:285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="480" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>User</th>
                      <th>Bank</th>
                      <th>Account No</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Requested</th>
                      <th>Transfer Ref</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="withdrawals.length">
                    <tr
                      v-for="(w, i) in withdrawals"
                      :key="`w_${i}`"
                      :class="{ 'table-row-even': i % 2 === 0, 'table-row-odd': i % 2 !== 0 }"
                    >
                      <td>{{ serialNoStarting + i + 1 }}</td>
                      <td>
                        <div class="font-weight-medium">
                          {{ w.user ? w.user.first_name + ' ' + w.user.last_name : '-' }}
                        </div>
                        <div class="caption grey--text">{{ w.user ? w.user.phone : '' }}</div>
                      </td>
                      <td>{{ w.bank_detail ? w.bank_detail.bank_name : '-' }}</td>
                      <td>
                        <span v-if="w.bank_detail">
                          {{ w.bank_detail.account_no }}
                          <div class="caption grey--text">IFSC: {{ w.bank_detail.ifsc_code }}</div>
                        </span>
                        <span v-else>-</span>
                      </td>
                      <td class="font-weight-bold">₹{{ w.amount }}</td>
                      <td>
                        <v-chip small :color="statusColor(w.status)" text-color="white">
                          {{ statusLabel(w.status) }}
                        </v-chip>
                      </td>
                      <td>{{ w.created_at | ddmmyyyy }}</td>
                      <td>
                        <span class="caption">{{ w.transfer_ref || '-' }}</span>
                      </td>
                      <td class="text-right" style="white-space:nowrap">
                        <!-- Pending: show Approve + Reject -->
                        <template v-if="w.status == 0">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs" v-on="on"
                                fab x-small color="green"
                                class="mx-1"
                                @click="openApproveDialog(w)"
                              >
                                <v-icon dark small>mdi-check</v-icon>
                              </v-btn>
                            </template>
                            <span>Approve</span>
                          </v-tooltip>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs" v-on="on"
                                fab x-small color="red"
                                class="mx-1"
                                @click="openRejectDialog(w)"
                              >
                                <v-icon dark small>mdi-close</v-icon>
                              </v-btn>
                            </template>
                            <span>Reject</span>
                          </v-tooltip>
                        </template>
                        <!-- Approved/Rejected: show view icon -->
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs" v-on="on"
                                fab x-small color="grey lighten-1"
                                class="mx-1"
                                @click="openDetailDialog(w)"
                              >
                                <v-icon small>mdi-eye</v-icon>
                              </v-btn>
                            </template>
                            <span>View Details</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="9" class="text-center py-8">
                        <v-icon size="48" color="grey lighten-2">mdi-bank-transfer-out</v-icon>
                        <p class="mt-2 grey--text">No withdrawal requests found</p>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Approve Dialog -->
      <v-dialog v-model="approveDialog" max-width="520">
        <v-card rounded="xxl" v-if="selectedWithdrawal">
          <v-card-title class="green darken-1 white--text">
            <v-icon left color="white">mdi-check-circle</v-icon>
            Approve Withdrawal
            <v-spacer></v-spacer>
            <v-btn icon dark @click="approveDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pt-4">
            <!-- Request Summary -->
            <v-simple-table dense class="mb-4">
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td class="grey--text">User</td>
                    <td class="font-weight-medium">
                      {{ selectedWithdrawal.user ? selectedWithdrawal.user.first_name + ' ' + selectedWithdrawal.user.last_name : '-' }}
                    </td>
                  </tr>
                  <tr>
                    <td class="grey--text">Bank</td>
                    <td>{{ selectedWithdrawal.bank_detail ? selectedWithdrawal.bank_detail.bank_name : '-' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Account No</td>
                    <td>{{ selectedWithdrawal.bank_detail ? selectedWithdrawal.bank_detail.account_no : '-' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">IFSC</td>
                    <td>{{ selectedWithdrawal.bank_detail ? selectedWithdrawal.bank_detail.ifsc_code : '-' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Amount</td>
                    <td class="font-weight-bold green--text text-h6">₹{{ selectedWithdrawal.amount }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-text-field
              v-model="approveForm.transfer_ref"
              label="Transfer Reference No (UTR / Transaction ID)"
              outlined
              placeholder="e.g. UTR123456789"
              hint="Enter the reference number from your bank / payment gateway"
              persistent-hint
            ></v-text-field>
            <v-textarea
              v-model="approveForm.remarks"
              label="Remarks (optional)"
              outlined
              rows="2"
              class="mt-3"
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="approveDialog = false">Cancel</v-btn>
            <v-btn color="green darken-1" dark :loading="isProcessing" @click="approveWithdrawal">
              <v-icon left>mdi-check</v-icon> Approve & Transfer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Reject Dialog -->
      <v-dialog v-model="rejectDialog" max-width="480">
        <v-card rounded="xxl" v-if="selectedWithdrawal">
          <v-card-title class="red darken-1 white--text">
            <v-icon left color="white">mdi-close-circle</v-icon>
            Reject Withdrawal
            <v-spacer></v-spacer>
            <v-btn icon dark @click="rejectDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="text-body-1 mb-3">
              Rejecting ₹<strong>{{ selectedWithdrawal.amount }}</strong> withdrawal request from
              <strong>{{ selectedWithdrawal.user ? selectedWithdrawal.user.first_name : '' }}</strong>.
            </p>
            <v-textarea
              v-model="rejectForm.remarks"
              label="Reason for rejection *"
              outlined
              rows="3"
              placeholder="e.g. Insufficient balance, Invalid bank details..."
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="pb-4 px-4">
            <v-spacer></v-spacer>
            <v-btn text @click="rejectDialog = false">Cancel</v-btn>
            <v-btn color="red darken-1" dark :loading="isProcessing" @click="rejectWithdrawal">
              <v-icon left>mdi-close</v-icon> Reject
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Detail Dialog (for already processed) -->
      <v-dialog v-model="detailDialog" max-width="480">
        <v-card rounded="xxl" v-if="selectedWithdrawal">
          <v-card-title class="primary white--text">
            Withdrawal Details
            <v-spacer></v-spacer>
            <v-btn icon dark @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pt-4">
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr>
                    <td class="grey--text">User</td>
                    <td>{{ selectedWithdrawal.user ? selectedWithdrawal.user.first_name + ' ' + selectedWithdrawal.user.last_name : '-' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Bank</td>
                    <td>{{ selectedWithdrawal.bank_detail ? selectedWithdrawal.bank_detail.bank_name : '-' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Account No</td>
                    <td>{{ selectedWithdrawal.bank_detail ? selectedWithdrawal.bank_detail.account_no : '-' }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Amount</td>
                    <td class="font-weight-bold">₹{{ selectedWithdrawal.amount }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Status</td>
                    <td>
                      <v-chip small :color="statusColor(selectedWithdrawal.status)" text-color="white">
                        {{ statusLabel(selectedWithdrawal.status) }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr v-if="selectedWithdrawal.transfer_ref">
                    <td class="grey--text">Transfer Ref</td>
                    <td class="font-weight-medium">{{ selectedWithdrawal.transfer_ref }}</td>
                  </tr>
                  <tr v-if="selectedWithdrawal.remarks">
                    <td class="grey--text">Remarks</td>
                    <td>{{ selectedWithdrawal.remarks }}</td>
                  </tr>
                  <tr>
                    <td class="grey--text">Processed</td>
                    <td>{{ selectedWithdrawal.processed_at | ddmmyyyy }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Success Dialog -->
      <v-dialog v-model="successDialog" width="380">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col class="text-center">
                <v-icon large :style="{ color: successColor, fontSize: '70px' }">{{ successIcon }}</v-icon>
                <h3 class="mt-2">{{ successMessage }}</h3>
                <v-btn :color="successColor" class="mt-3" dark @click="successDialog = false">OK</v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "WithdrawalsPage",
  filters: {
    formatAmount(val) {
      if (!val) return "0.00";
      return Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2 });
    },
  },
  data() {
    return {
      withdrawals: [],
      filter: { search_keyword: "", status: null },
      page: 1,
      count: 0,
      serialNoStarting: 0,
      paginationLength: 0,
      isLoading: false,
      isProcessing: false,
      // Stats
      pendingCount: 0,
      pendingAmount: 0,
      approvedCount: 0,
      approvedAmount: 0,
      rejectedCount: 0,
      // Dialogs
      approveDialog: false,
      rejectDialog: false,
      detailDialog: false,
      successDialog: false,
      selectedWithdrawal: null,
      approveForm: { transfer_ref: "", remarks: "" },
      rejectForm: { remarks: "" },
      successMessage: "",
      successIcon: "mdi-check-circle",
      successColor: "green",
      statusOptions: [
        { text: "Pending",  value: "0" },
        { text: "Approved", value: "1" },
        { text: "Rejected", value: "2" },
      ],
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    statusColor(status) {
      if (status == 0) return "orange";
      if (status == 1) return "green";
      if (status == 2) return "red";
      return "grey";
    },
    statusLabel(status) {
      if (status == 0) return "Pending";
      if (status == 1) return "Approved";
      if (status == 2) return "Rejected";
      return "Unknown";
    },
    async getData() {
      this.isLoading = true;
      const statusParam =
        this.filter.status !== null && this.filter.status !== undefined
          ? "&status=" + this.filter.status
          : "";
      const query =
        "page=" + this.page +
        "&rowsPerPage=" + this.rowsPerPage +
        "&search_keyword=" + (this.filter.search_keyword || "") +
        statusParam;
      try {
        const res = await axios.get(`withdrawals?${query}`);
        this.withdrawals     = res.data.data || [];
        this.count           = res.data.count;
        this.pendingCount    = res.data.pending_count;
        this.pendingAmount   = res.data.pending_amount;
        this.approvedCount   = res.data.approved_count;
        this.approvedAmount  = res.data.approved_amount;
        this.rejectedCount   = this.count - this.pendingCount - this.approvedCount;
        this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (e) {
        console.log(e);
      }
      this.isLoading = false;
    },
    openApproveDialog(w) {
      this.selectedWithdrawal = w;
      this.approveForm = { transfer_ref: "", remarks: "" };
      this.approveDialog = true;
    },
    openRejectDialog(w) {
      this.selectedWithdrawal = w;
      this.rejectForm = { remarks: "" };
      this.rejectDialog = true;
    },
    openDetailDialog(w) {
      this.selectedWithdrawal = w;
      this.detailDialog = true;
    },
    async approveWithdrawal() {
      this.isProcessing = true;
      try {
        await axios.post(`withdrawals/${this.selectedWithdrawal.id}/approve`, {
          transfer_ref: this.approveForm.transfer_ref,
          remarks: this.approveForm.remarks,
        });
        this.approveDialog = false;
        this.successMessage = "Withdrawal approved! Amount will be transferred to user's bank account.";
        this.successIcon    = "mdi-check-circle";
        this.successColor   = "green";
        this.successDialog  = true;
        this.getData();
      } catch (e) {
        console.log(e);
      }
      this.isProcessing = false;
    },
    async rejectWithdrawal() {
      this.isProcessing = true;
      try {
        await axios.post(`withdrawals/${this.selectedWithdrawal.id}/reject`, {
          remarks: this.rejectForm.remarks,
        });
        this.rejectDialog  = false;
        this.successMessage = "Withdrawal request has been rejected.";
        this.successIcon    = "mdi-close-circle";
        this.successColor   = "red";
        this.successDialog  = true;
        this.getData();
      } catch (e) {
        console.log(e);
      }
      this.isProcessing = false;
    },
  },
};
</script>
